import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, SystemHealth, DatabaseStats } from '../../services/admin';
import { useProcessPolling } from '../../hooks/useProcessPolling';
import { ProcessCard } from '../../components/admin/ProcessCard';
import { PendingWorkCard } from '../../components/admin/PendingWorkCard';
import { DiskUsageCard } from '../../components/admin/DiskUsageCard';
import { DataFreshnessCard } from '../../components/admin/DataFreshnessCard';
import { ErrorLogsCard } from '../../components/admin/ErrorLogsCard';

// Map of process types that require confirmation with their phrases
const DANGEROUS_PROCESSES: Record<string, string> = {
  saos_fetch: 'SAOS FETCH',
  cbosa_fetch: 'CBOSA FETCH',
  ingestion: 'INGESTION',
  embeddings: 'EMBEDDINGS',
  chunk_embeddings: 'CHUNK EMBEDDINGS',
  reprocessing: 'REPROCESSING',
};

export default function AdminSystemPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const {
    processes,
    pendingWork,
    freshness,
    diskUsage,
    errors,
    isLoading: processesLoading,
    lastUpdated,
    refetch: refetchProcesses,
    hasRunningProcess,
  } = useProcessPolling();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [healthData, statsData] = await Promise.all([
          adminAPI.getSystemHealth(),
          adminAPI.getDatabaseStats(),
        ]);
        setHealth(healthData);
        setStats(statsData);
      } catch (err) {
        console.error('Failed to fetch system data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);

    return parts.join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('admin.system')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">{t('adminSystem.subtitle')}</p>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-3 h-3 rounded-full ${health?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
            <h3 className="font-semibold text-slate-900 dark:text-white">{t('common.status')}</h3>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white capitalize">
            {health?.status || 'Unknown'}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">{t('adminSystem.uptime')}</h3>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {health?.process.uptime ? formatUptime(health.process.uptime) : '-'}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Node.js</h3>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {health?.process.nodeVersion || '-'}
          </p>
        </div>
      </div>

      {/* Processes Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('adminSystem.processes')}</h2>
            {hasRunningProcess && (
              <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                {t('adminSystem.activeProcesses')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {t('adminSystem.lastUpdate', { time: lastUpdated.toLocaleTimeString(locale) })}
              </span>
            )}
            <button
              onClick={refetchProcesses}
              disabled={processesLoading}
              className="p-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title={t('common.refresh')}
            >
              <svg className={`w-5 h-5 ${processesLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {processes.map((process) => {
            const confirmPhrase = DANGEROUS_PROCESSES[process.processType];
            return (
              <ProcessCard
                key={process.processType}
                process={process}
                onRefresh={refetchProcesses}
                requiresConfirmation={!!confirmPhrase}
                confirmPhrase={confirmPhrase}
              />
            );
          })}
        </div>
      </div>

      {/* Pending Work & Data Freshness */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <PendingWorkCard data={pendingWork} />
        <DataFreshnessCard data={freshness} />
      </div>

      {/* Memory, Database Pool, and Disk */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Memory */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminSystem.memory')}</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-600 dark:text-slate-400">Heap Used</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {health?.process.memoryUsage ? formatBytes(health.process.memoryUsage.heapUsed) : '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600 dark:text-slate-400">Heap Total</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {health?.process.memoryUsage ? formatBytes(health.process.memoryUsage.heapTotal) : '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600 dark:text-slate-400">RSS</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {health?.process.memoryUsage ? formatBytes(health.process.memoryUsage.rss) : '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600 dark:text-slate-400">External</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {health?.process.memoryUsage ? formatBytes(health.process.memoryUsage.external) : '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Database Pool */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminSystem.database')}</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-600 dark:text-slate-400">{t('common.size')}</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {health?.database.size || '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600 dark:text-slate-400">Pool Total</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {health?.database.pool.totalCount ?? '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600 dark:text-slate-400">Pool Idle</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {health?.database.pool.idleCount ?? '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600 dark:text-slate-400">Pool Waiting</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {health?.database.pool.waitingCount ?? '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Disk Usage */}
        <DiskUsageCard data={diskUsage} />
      </div>

      {/* Error Logs */}
      <div className="mb-8">
        <ErrorLogsCard errors={errors} />
      </div>

      {/* Database Stats */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminSystem.dataStats')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('adminSystem.judgments')}</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {stats?.judgments.total.toLocaleString() || 0}
            </p>
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('adminSystem.chunks')}</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {stats?.chunks.total.toLocaleString() || 0}
            </p>
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('adminSystem.withEmbeddings')}</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {stats?.chunks.withEmbeddings.toLocaleString() || 0}
            </p>
          </div>
        </div>

        {/* Court type distribution */}
        {stats?.judgments.byCourtType && Object.keys(stats.judgments.byCourtType).length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              {t('adminSystem.judgmentsByCourtType')}
            </h3>
            <div className="space-y-2">
              {Object.entries(stats.judgments.byCourtType).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">{t(`adminSystem.courtTypes.${type}`, { defaultValue: type })}</span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table Sizes */}
      {health?.database.tables && health.database.tables.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-8">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('adminSystem.tableSizes')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                    {t('common.table')}
                  </th>
                  <th className="text-right py-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                    {t('common.size')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {health.database.tables.map((table) => (
                  <tr key={table.table_name} className="border-b border-slate-100 dark:border-slate-700/50">
                    <td className="py-2 text-slate-900 dark:text-white font-mono text-sm">
                      {table.table_name}
                    </td>
                    <td className="py-2 text-right text-slate-600 dark:text-slate-400">
                      {table.total_size}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
